import type { EncounterAccepted, World, WorldPresence } from '@mezha/api'
import { baseApi } from '../baseApi'

const toWorld = (dispatch: (a: unknown) => unknown, data: World) => {
  dispatch(worldApi.util.upsertQueryData('world', undefined, data))
  dispatch(baseApi.util.invalidateTags(['Hero']))
}

type Versioned = { expected_version: number }

export const worldApi = baseApi.injectEndpoints({
  endpoints: b => ({
    worldPresence: b.mutation<WorldPresence, void>({
      query: () => ({ command: '/api/v1/world/presence' }),
      invalidatesTags: data => data?.battle_id ? ['ActiveBattle', 'World', 'Hero'] : [],
    }),
    world: b.query<World, void>({ query: () => ({ get: '/api/v1/world' }), providesTags: ['World'] }),
    move: b.mutation<World, Versioned & { x: number; y: number }>({
      query: body => ({ command: '/api/v1/world/move', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => toWorld(dispatch, (await queryFulfilled).data),
    }),
    enter: b.mutation<World, Versioned & { place_id: string }>({
      query: body => ({ command: '/api/v1/world/enter', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => toWorld(dispatch, (await queryFulfilled).data),
    }),
    leave: b.mutation<World, Versioned>({
      query: body => ({ command: '/api/v1/world/leave', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => toWorld(dispatch, (await queryFulfilled).data),
    }),
    cellAction: b.mutation<World, Versioned & { action: string }>({
      query: body => ({ command: '/api/v1/world/action', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => toWorld(dispatch, (await queryFulfilled).data),
    }),
    acceptEncounter: b.mutation<EncounterAccepted, Versioned & { encounter_id: string }>({
      query: body => ({ command: '/api/v1/world/encounter/accept', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        toWorld(dispatch, data.world)
        dispatch(baseApi.util.invalidateTags(['ActiveBattle']))
      },
    }),
    declineEncounter: b.mutation<World, Versioned & { encounter_id: string }>({
      query: body => ({ command: '/api/v1/world/encounter/decline', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => toWorld(dispatch, (await queryFulfilled).data),
    }),
  }),
})

export const {
  useWorldQuery, useMoveMutation, useEnterMutation, useLeaveMutation, useCellActionMutation,
  useAcceptEncounterMutation, useDeclineEncounterMutation,
  useWorldPresenceMutation,
} = worldApi
